import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";

function MultiSelectField({ testid, options, value, onChange, placeholder = "Select..." }) {
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter((x) => x !== val) : [...selected, val]);
  const opt = (o) => (typeof o === "string" ? { value: o, label: o } : o);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="mt-1 flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input px-2 py-1.5 text-left text-sm">
          {selected.length === 0 ? <span className="text-muted-foreground">{placeholder}</span> :
            options.map(opt).filter((o) => selected.includes(o.value)).map((o) => (
              <span key={o.value} className="rounded bg-muted px-1.5 py-0.5 text-xs">{o.label}</span>
            ))}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map(opt).map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => toggle(o.value)}>
                  <input type="checkbox" readOnly checked={selected.includes(o.value)} className="mr-2" />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Reusable schema-driven form dialog.
 * fields: [{ name, label, type: text|number|textarea|date|select|multiselect|email|tel, options?, required?, full?, placeholder? }]
 * multiselect stores its value as an array of option values.
 */
export function FormDialog({ title, trigger, fields, initial = {}, submitLabel = "Save", onSubmit, testid }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial || {}); /* eslint-disable-next-line */ }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      const v = form[f.name];
      const empty = f.type === "multiselect" ? !Array.isArray(v) || v.length === 0 : (v === undefined || v === "" || v === null);
      if (f.required && empty) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    try {
      setSaving(true);
      await onSubmit(form);
      setOpen(false);
      setForm(initial || {});
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.name} className={f.full || f.type === "textarea" ? "col-span-2" : ""}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`field-${f.name}`} className="mt-1" placeholder={f.placeholder}
                  value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />
              ) : f.type === "multiselect" ? (
                <MultiSelectField testid={`field-${f.name}`} options={f.options} value={form[f.name]}
                  onChange={(v) => set(f.name, v)} placeholder={f.placeholder} />
              ) : f.type === "select" ? (
                <Select value={form[f.name] != null ? String(form[f.name]) : undefined} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="mt-1" data-testid={`field-${f.name}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => {
                      const val = typeof o === "string" ? o : o.value;
                      const lab = typeof o === "string" ? o : o.label;
                      return <SelectItem key={val} value={String(val)} className="capitalize">{lab}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`field-${f.name}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  className="mt-1" placeholder={f.placeholder} value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid={`${testid}-submit`} onClick={submit} disabled={saving}>{submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
